import React from 'react';
import { OutputItem } from '../types';

interface TerminalOutputProps {
  output: OutputItem[];
  onOpenPage: (pageId: string) => void;
}

type InlineGroup = { type: 'inline-group'; items: OutputItem[] };
type GroupedItem = OutputItem | InlineGroup;

const TerminalOutput: React.FC<TerminalOutputProps> = ({ output, onOpenPage }) => {
  // Group consecutive inline items together
  const groupedOutput: GroupedItem[] = [];
  let currentInlineGroup: OutputItem[] = [];
  
  for (let i = 0; i < output.length; i++) {
    const item = output[i];
    if (item.inline) {
      currentInlineGroup.push(item);
    } else {
      // If we have accumulated inline items, add them as a group
      if (currentInlineGroup.length > 0) {
        groupedOutput.push({ type: 'inline-group', items: currentInlineGroup });
        currentInlineGroup = [];
      }
      // Add the non-inline item
      groupedOutput.push(item);
    }
  }
  
  // Don't forget the last group if it exists
  if (currentInlineGroup.length > 0) {
    groupedOutput.push({ type: 'inline-group', items: currentInlineGroup });
  }

  // Helper function to execute a command when clicked
  const handleCommandClick = (command: string) => {
    // Find the input element and set its value
    const inputElement = document.querySelector('.terminal-input') as HTMLInputElement;
    if (inputElement) {
      // Set the value
      inputElement.value = command;

      // Find the form and submit it
      const formElement = inputElement.closest('form');
      if (formElement) {
        formElement.dispatchEvent(new Event('submit', { bubbles: true }));
      }
    }
  };

  return (
    <div className="terminal-output">
      {groupedOutput.map((item, index) => {
        // Handle inline groups
        if (item.type === 'inline-group') {
          return (
            <div key={`inline-group-${index}`} className="terminal-text text-sm sm:text-base md:text-lg mb-1">
              {item.items.map((inlineItem, inlineIndex) => {
                if (inlineItem.type === 'text') {
                  return <span key={`${inlineItem.key}-${inlineIndex}`}>{inlineItem.content}</span>;
                } else if (inlineItem.type === 'link') {
                  return (
                    <span
                      key={`${inlineItem.key}-${inlineIndex}`}
                      className="cursor-pointer underline hover:text-white text-cyan-300 font-semibold"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (inlineItem.onClick) {
                          inlineItem.onClick();
                        } else {
                          // Convert command to lowercase for page opening
                          const pageId = inlineItem.content.toLowerCase();
                          onOpenPage(pageId);
                        }
                      }}
                    >
                      {inlineItem.content}
                    </span>
                  );
                }
                return null;
              })}
            </div>
          );
        }
        
        // Handle regular items
        switch (item.type) {
          case 'text':
            return (
              <div key={item.key} className="terminal-text text-sm sm:text-base md:text-lg mb-1">
                {item.content}
              </div>
            );
          case 'command':
            return (
              <div key={item.key} className="terminal-text text-sm sm:text-base md:text-lg mb-1">
                <span className="text-yellow-300">GB4K:&gt;</span>{' '}
                <span 
                  className="cursor-pointer hover:text-white"
                  onClick={() => handleCommandClick(item.content)}
                >
                  {item.content}
                </span>
              </div>
            );
          case 'error':
            return (
              <div key={item.key} className="terminal-text text-red-400 text-sm sm:text-base md:text-lg mb-1">
                ?ERROR: {item.content}
              </div>
            );
          case 'link':
            return (
              <div key={item.key} className="terminal-text text-sm sm:text-base md:text-lg mb-1">
                <span 
                  className="cursor-pointer underline hover:text-white text-cyan-300 font-semibold"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      // Convert command to lowercase for page opening
                      const pageId = item.content.toLowerCase();
                      onOpenPage(pageId);
                    }
                  }}
                >
                  {item.content}
                </span>
              </div>
            );
          case 'clear':
            return null;
          default:
            return null;
        }
      })}
    </div>
  );
};

export default TerminalOutput;